import { map, mergeMap, catchError, tap, filter, mapTo } from 'rxjs/operators'
import { forkJoin, of } from 'rxjs'
import { ofType, combineEpics } from 'redux-observable'

import { logger } from '../../util/log.helpers'
import {
  DOWNLOAD_ACTION,
  TRANSACTION_ACTIONS,
  TRANSACTION_DOWNLOAD_ACTIONS
} from '../../constants/actions'
import apiService from '../../services/api'
import {
  buildOffsetRangeForDownload,
  buildPaginationFilters,
  extractTokenId,
  extractTokenInfo,
  extractTransactionsPagination,
  extractTransactionsProfile,
  formatTransactions
} from '../../util/epics.helpers'
import {
  commonParser,
  parseTransactionsResponse
} from '../../util/apiParser.helpers'
import { wrapUserAccessToken } from '../../util/auth.helpers'
import {
  ALLOW_MAX_RECORDS_FOR_DOWNLOAD,
  DOWNLOAD_REPORT_TYPES,
  MAX_NUMBER_OF_TRANSACTIONS_FOR_DOWNLOAD
} from '../../constants/app'
import { buildDefaultParamsWithFilters } from '../../util/transaction/transaction.helpers'
import { buildDownloadFileName } from '../../util/download.helpers'
import { ANALYTICS_INTERVAL_FILTERS } from '../../constants/analytics'
import {
  analyticsFilterData,
  buildDateRangeQueryParams,
  isAnalyticsPointsBank
} from '../../util/analytics/analytics.helpers'

const handleGetTransactionsHistoryEpic = (action$, state$) =>
  action$.pipe(
    ofType(TRANSACTION_ACTIONS.ON_GET_TRANSACTIONS),
    mergeMap(({ payload }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .getTransactions(
            buildTransactionPaginationFilters(payload, state$),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => ({
              type: TRANSACTION_ACTIONS.ON_GET_TRANSACTIONS_SUCCESS,
              payload: parseTransactionsResponse(response.data)
            })),
            catchError((err) =>
              of({
                type: TRANSACTION_ACTIONS.ON_GET_TRANSACTIONS_FAILED,
                payload: err
              })
            )
          )
      )
    )
  )

const handleGetTransactionsChartEpic = (action$, state$) =>
  action$.pipe(
    ofType(TRANSACTION_ACTIONS.ON_GET_EXCHANGE_ACTIVITY_CHART),
    mergeMap(() =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .getTransactionsChart(
            buildExchangeActivityChartParams(state$),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => ({
              type: TRANSACTION_ACTIONS.ON_GET_EXCHANGE_ACTIVITY_CHART_SUCCESS,
              payload: commonParser(response.data)
            })),
            catchError((err) =>
              of({
                type: TRANSACTION_ACTIONS.ON_GET_EXCHANGE_ACTIVITY_CHART_FAILED,
                payload: err
              })
            )
          )
      )
    )
  )

const handleGetTransactionsChartByFilterEpic = (action$, state$) =>
  action$.pipe(
    ofType(TRANSACTION_ACTIONS.ON_GET_TRANSACTIONS_CHART),
    mergeMap(() =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .getTransactionsChart(
            buildTransactionChartParams(state$),
            accessToken
          )
          .pipe(
            map(({ response }) => ({
              type: TRANSACTION_ACTIONS.ON_GET_TRANSACTIONS_CHART_SUCCESS,
              payload: {
                chartData: commonParser(response.data),
                tokenProfile: extractTokenInfo(state$),
                analyticsFilter: analyticsFilterData(state$)
              }
            })),
            catchError((err) =>
              of({
                type: TRANSACTION_ACTIONS.ON_GET_TRANSACTIONS_CHART_FAILED,
                payload: err
              })
            )
          )
      )
    )
  )

const handleTransactionsForDownloadEpic = (action$, state$) =>
  action$.pipe(
    ofType(TRANSACTION_DOWNLOAD_ACTIONS.ON_GET_TRANSACTIONS_FOR_DOWNLOAD),
    map(() => extractTransactionsPagination(state$)),
    filter(
      ({ totalTxCount, recordsPerPage }) =>
        totalTxCount <= ALLOW_MAX_RECORDS_FOR_DOWNLOAD &&
        totalTxCount > recordsPerPage
    ),
    mergeMap(({ totalTxCount }) =>
      wrapUserAccessToken((accessToken) =>
        getAllTransactionsForDownload(totalTxCount, state$, accessToken).pipe(
          map((response) => {
            return {
              type: TRANSACTION_DOWNLOAD_ACTIONS.ON_GET_TRANSACTIONS_FOR_DOWNLOAD_SUCCESS,
              payload: response
            }
          }),
          catchError((err) =>
            of({
              type: TRANSACTION_DOWNLOAD_ACTIONS.ON_GET_TRANSACTIONS_FOR_DOWNLOAD_FAILED,
              payload: err
            })
          )
        )
      )
    )
  )

const handleTransactionsForDownloadBelowThresholdEpic = (action$, state$) =>
  action$.pipe(
    ofType(TRANSACTION_DOWNLOAD_ACTIONS.ON_GET_TRANSACTIONS_FOR_DOWNLOAD),
    map(() => extractTransactionsProfile(state$)),
    filter(
      ({ pagination: { totalTxCount, recordsPerPage } }) =>
        totalTxCount <= recordsPerPage
    ),
    map(({ transactions }) => {
      return {
        type: TRANSACTION_DOWNLOAD_ACTIONS.ON_GET_TRANSACTIONS_FOR_DOWNLOAD_SUCCESS,
        payload: transactions
      }
    })
  )

const getAllTransactionsForDownload = (totalTxCount, state, accessToken) => {
  const { limit, ...restQueryAttributes } =
    buildTransactionsQueriesForDownload(state)
  return forkJoin(
    buildOffsetRangeForDownload(totalTxCount, limit).map((offset) =>
      apiService.getTransactions(
        { offset, ...restQueryAttributes },
        accessToken
      )
    )
  ).pipe(
    map((responseArray) =>
      responseArray.flatMap(({ response }) =>
        formatTransactions(response.data.transactions)
      )
    )
  )
}

const handleCheckConditionForReportEmailEpic = (action$, state$) =>
  action$.pipe(
    ofType(TRANSACTION_DOWNLOAD_ACTIONS.ON_GET_TRANSACTIONS_FOR_DOWNLOAD),
    map(() => extractTransactionsPagination(state$)),
    filter(({ totalTxCount }) => totalTxCount > ALLOW_MAX_RECORDS_FOR_DOWNLOAD),
    mapTo({
      type: TRANSACTION_DOWNLOAD_ACTIONS.ON_SEND_REPORT_IN_EMAIL
    })
  )

const handleTransactionsReportEmailEpic = (action$, state$) =>
  action$.pipe(
    ofType(TRANSACTION_DOWNLOAD_ACTIONS.ON_SEND_REPORT_IN_EMAIL),
    mergeMap(() =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .downloadReportToEmail(
            DOWNLOAD_REPORT_TYPES.TRANSACTIONS,
            buildTransactionsQueriesForReportEmail(state$),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => ({
              type: DOWNLOAD_ACTION.ON_SENT_REPORT_IN_EMAIL_SUCCESS,
              payload: response
            })),
            catchError((err) =>
              of({
                type: DOWNLOAD_ACTION.ON_SENT_REPORT_IN_EMAIL_FAILED,
                payload: err
              })
            )
          )
      )
    )
  )

export const transactionEpic = combineEpics(
  handleGetTransactionsHistoryEpic,
  handleGetTransactionsChartEpic,
  handleTransactionsForDownloadEpic,
  handleTransactionsForDownloadBelowThresholdEpic,
  handleCheckConditionForReportEmailEpic,
  handleTransactionsReportEmailEpic,
  handleGetTransactionsChartByFilterEpic
)

const buildTransactionPaginationFilters = (payload, state) => {
  const pagination = extractTransactionsPagination(state)
  return {
    ...buildPaginationFilters(payload, pagination),
    ...buildDefaultParamsWithFilters(state)
  }
}

const buildTransactionsQueriesForDownload = (state) => {
  const limit = MAX_NUMBER_OF_TRANSACTIONS_FOR_DOWNLOAD
  return {
    limit,
    ...buildDefaultParamsWithFilters(state)
  }
}

const buildTransactionsQueriesForReportEmail = (state) => {
  const {
    types,
    brandId,
    from_time,
    to_time,
    token_id,
    token_to_id,
    exclude_token_to_types
  } = buildDefaultParamsWithFilters(state)
  const tokenProfile = extractTokenInfo(state)
  const analyticsFilter = analyticsFilterData(state)
  const isExchangeAnalytics = !isAnalyticsPointsBank(analyticsFilter.type)
  return {
    brand_id: brandId,
    ...(types.length > 0 && { types }),
    ...(token_id && { token_id }),
    ...(token_to_id && { token_to_id }),
    ...(isExchangeAnalytics && { exclude_token_to_types }),
    from_time,
    to_time,
    filename: buildDownloadFileName(
      tokenProfile.name,
      DOWNLOAD_REPORT_TYPES.TRANSACTIONS
    ),
    show_txn_for_exchange: isExchangeAnalytics
  }
}

const buildExchangeActivityChartParams = (state) => {
  const tokenId = extractTokenId(state)
  return {
    tokenId,
    time_interval: ANALYTICS_INTERVAL_FILTERS.MONTHLY
  }
}

const buildTransactionChartParams = (state) => {
  const tokenId = extractTokenId(state)
  const analyticsFilter = analyticsFilterData(state)
  return {
    tokenId,
    ...buildDateRangeQueryParams(analyticsFilter),
    time_interval: analyticsFilter.interval
  }
}
